// @flow

import { Component } from '../jsti/core';
import EventNames from '../constants/EventNames';

/**
 * Toggle component
 *
 * Component element is the trigger
 * Can have multiple targets, targets are reachable by reference
 * Can have multiple references: [data-reference="target another-target"]
 * Toggles the targets from classes (mostly used for showing/hiding of elements)
 *
 * Required: [reference, targets]
 * Configurable: [class]
 */
export default class Toggle extends Component {
  #clickHandler: function;
  targets: Array<HTMLElement>;
  class: string = 'hidden';

  /**
   * Toggle the class of the target
   */
  static toggle(target: HTMLElement, className: string = 'hidden') {
    target.classList.toggle(className);
  }

  /**
   * Force show the target
   */
  static show(target: HTMLElement, className: string = 'hidden') {
    target.classList.remove(className);
  }

  /**
   * Force hide the target
   */
  static hide(target: HTMLElement, className: string = 'hidden') {
    target.classList.add(className);
  }

  /**
   * Initialize the component
   *
   * @param pEl
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);
    this.setUp();
    this.#clickHandler = this.onClick.bind(this);
    this.addEventListener(EventNames.CLICK, this.#clickHandler);
  }

  /**
   * Validate and setup properties
   */
  setUp(): void {
    const customClass: ?string = this.el.dataset.class;
    if (customClass) {
      this.class = customClass;
    }

    const ref: ?string = this.el.dataset.reference;
    if (!ref) {
      throw new Error('Target reference is required!');
    }

    this.initializeTargets(ref.split(' '));
  }

  /**
   *
   */
  initializeTargets(references: Array<string>): void {
    const targetList: Array<HTMLElement> = [];
    for (const reference of references) {
      const targets: NodeList<HTMLElement> = document.querySelectorAll(`[ref=${reference}]`);

      if (!targets.length) {
        throw new Error(`At least one target of ${reference} is required!`);
      }

      for (const target of targets) {
        targetList.push(target);
      }
    }

    this.targets = targetList;
  }

  /**
   * Toggle the targets on click
   */
  onClick(e: Event): void {
    e.preventDefault();
    for (const target of this.targets) {
      this.toggle(target);
    }
  }

  /**
   * Toggle the class of the target
   *
   * @param target
   */
  toggle(target: HTMLElement) {
    target.classList.toggle(this.class);
  }
}
